/**
 * The campaign group a container editor works in: the group open on the
 * page, else the group of the master config it follows, else the group of
 * its own campaign. Only that group's campaigns are offered, and its master
 * config is the one the container follows — picked for it, never chosen.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DropGenMasterSummary } from '@trafficpulse/shared';

import type { GroupIndex } from '../../../campaign/group-index';
import { useCampaignGroups } from '../../../campaign/use-campaign-groups';
import type { ContainerEditor } from '../../state/useContainerEditor';
import { useSectionSources } from '../section-sources';

export interface EditorGroup {
  ix: GroupIndex;
  loaded: boolean;
  /** null = no group open and nothing to derive one from (a new container on All). */
  key: string | null;
  label: string;
  fullLabel: string;
  members: { wiserId: number; name: string }[];
  /** The group's master config; the followed one wins when it is in the group. */
  master: DropGenMasterSummary | null;
}

interface GroupInputs {
  ix: GroupIndex;
  pageKey: string | null;
  followed: DropGenMasterSummary | null;
  masters: DropGenMasterSummary[];
  campaignWiserId: number | null;
}

/** Pure: the editor's group from what the page, the draft and the plan know. */
export function resolveEditorGroup({ ix, pageKey, followed, masters, campaignWiserId }: GroupInputs) {
  const keyOfMaster = (m: DropGenMasterSummary) => m.campaignGroupKey ?? ix.keyOf(m.campaignWiserId);
  const key = pageKey ?? (followed ? keyOfMaster(followed) : null) ?? (campaignWiserId !== null ? ix.keyOf(campaignWiserId) : null);
  const group = key === null ? null : ix.group(key);
  const master =
    followed && keyOfMaster(followed) === key ? followed : (masters.find((m) => key !== null && keyOfMaster(m) === key) ?? null);
  return {
    key,
    label: key === null ? '' : ix.labelOf(key),
    fullLabel: group?.fullLabel ?? '',
    members: group?.campaigns.map((c) => ({ wiserId: c.wiserId, name: c.name })) ?? [],
    master,
  };
}

export function useEditorGroup(editor: ContainerEditor, countryKey: string): EditorGroup {
  const sources = useSectionSources();
  const ix = useCampaignGroups(countryKey);
  const resolved = resolveEditorGroup({
    ix,
    pageKey: sources.groupKey,
    followed: sources.masterRef,
    masters: sources.masters,
    campaignWiserId: editor.draft.campaignWiserId,
  });
  return { ix, loaded: ix.loaded, ...resolved };
}
